import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../dashboard/components/DashboardLayout';
import PartnerList from './components/PartnerList';
import PartnerDetail from './components/PartnerDetail';
import PartnerRanking from './components/PartnerRanking';
import CreatePartnerForm from './components/CreatePartnerForm';

export interface PartnerSales {
  partnerId: string;
  partnerName: string;
  email: string;
  commissionRate: number;
  totalOrders: number;
  totalRevenue: number;
  totalCommission: number;
  thisWeekRevenue: number;
  thisMonthRevenue: number;
  thisYearRevenue: number;
  lastOrderDate: string;
  isActive: boolean;
  status: 'pending' | 'approved' | 'rejected';
  topProducts: {
    productName: string;
    quantity: number;
    revenue: number;
  }[];
}

export default function PartnersPage() {
  const { userProfile } = useAuth();
  const [partnersData, setPartnersData] = useState<PartnerSales[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<'list' | 'detail' | 'ranking'>('list');
  const [selectedPartner, setSelectedPartner] = useState<PartnerSales | null>(null);
  const [timeFilter, setTimeFilter] = useState<'week' | 'month' | 'year'>('month');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<PartnerSales | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [partnerToApprove, setPartnerToApprove] = useState<PartnerSales | null>(null);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending'>('all');
  const [sortBy, setSortBy] = useState<'revenue' | 'orders' | 'commission'>('revenue');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Tải dữ liệu từ Supabase
  const fetchPartners = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedPartners: PartnerSales[] = (data || []).map(partner => ({
        partnerId: partner.id,
        partnerName: partner.partner_name,
        email: partner.email,
        commissionRate: partner.commission_rate || 0,
        totalOrders: partner.total_orders || 0,
        totalRevenue: partner.total_revenue || 0,
        totalCommission: partner.total_commission || 0,
        thisWeekRevenue: partner.this_week_revenue || 0,
        thisMonthRevenue: partner.this_month_revenue || 0,
        thisYearRevenue: partner.this_year_revenue || 0,
        lastOrderDate: partner.last_order_date || '',
        isActive: partner.is_active,
        status: partner.status || 'pending',
        topProducts: [] // Sẽ được tính toán từ dữ liệu đơn hàng
      }));

      setPartnersData(formattedPartners);
    } catch (error) {
      console.error('Lỗi khi tải dữ liệu đối tác:', error);
      alert('Không thể tải dữ liệu đối tác. Vui lòng thử lại!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const filteredPartners = partnersData
    .filter(partner => {
      const matchesSearch = partner.partnerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           partner.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || 
                           (statusFilter === 'active' && partner.isActive && partner.status === 'approved') ||
                           (statusFilter === 'inactive' && !partner.isActive && partner.status === 'approved') ||
                           (statusFilter === 'pending' && partner.status === 'pending');
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'revenue':
          return b.totalRevenue - a.totalRevenue;
        case 'orders':
          return b.totalOrders - a.totalOrders;
        case 'commission':
          return b.totalCommission - a.totalCommission;
        default:
          return 0;
      }
    });

  const handleApprovePartner = async (partnerId: string, action: 'approve' | 'reject') => {
    try {
      const { error } = await supabase
        .from('partners')
        .update({
          status: action === 'approve' ? 'approved' : 'rejected',
          is_active: action === 'approve',
          updated_at: new Date().toISOString()
        })
        .eq('id', partnerId);

      if (error) throw error;

      await fetchPartners(); // Tải lại dữ liệu
      setShowApprovalModal(false);
      setPartnerToApprove(null);
      
      alert(action === 'approve' ? 'Phê duyệt đối tác thành công!' : 'Từ chối đối tác thành công!');
    } catch (error) {
      console.error('Lỗi khi phê duyệt đối tác:', error);
      alert('Không thể phê duyệt đối tác. Vui lòng thử lại!');
    }
  };

  const handleDeletePartner = async (partnerId: string) => {
    try {
      // Xóa từ bảng partners trước
      const { error: partnerError } = await supabase
        .from('partners')
        .delete()
        .eq('id', partnerId);

      if (partnerError) throw partnerError;

      // Xóa từ bảng users
      const { error: userError } = await supabase
        .from('users')
        .delete()
        .eq('auth_user_id', partnerId);

      if (userError) throw userError;

      await fetchPartners(); // Tải lại dữ liệu
      setShowDeleteModal(false);
      setPartnerToDelete(null);
      
      // Nếu đang xem chi tiết partner bị xóa, quay về danh sách
      if (selectedPartner && selectedPartner.partnerId === partnerId) {
        setCurrentView('list');
        setSelectedPartner(null);
      }
      
      alert('Xóa đối tác thành công!');
    } catch (error) {
      console.error('Lỗi khi xóa đối tác:', error);
      alert('Không thể xóa đối tác. Vui lòng thử lại!');
    }
  };

  const handleCreatePartner = () => {
    setShowCreateForm(true);
  };

  const handleCreateSubmit = async (partnerData: any) => {
    try {
      setIsLoading(true);
      
      // Create user account first
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: partnerData.email,
        password: partnerData.password,
      });

      if (authError) throw authError;

      if (authData.user) {
        // Create user profile
        const { error: profileError } = await supabase
          .from('users')
          .insert({
            id: authData.user.id,
            email: partnerData.email,
            full_name: partnerData.full_name,
            role: 'partner',
            is_active: true
          });

        if (profileError) throw profileError;

        // Create partner record
        const { error: partnerError } = await supabase
          .from('partners')
          .insert({
            user_id: authData.user.id,
            name: partnerData.full_name,
            email: partnerData.email,
            phone: partnerData.phone,
            address: partnerData.address,
            commission_rate: partnerData.commission_rate,
            notes: partnerData.notes,
            status: 'approved', // Manager created partners are auto-approved
            is_active: true
          });

        if (partnerError) throw partnerError;

        setShowCreateForm(false);
        fetchPartners();
        alert('Tạo đối tác thành công!');
      }
    } catch (error: any) {
      console.error('Error creating partner:', error);
      alert('Có lỗi xảy ra khi tạo đối tác: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDeletePartner = (partner: PartnerSales) => {
    setPartnerToDelete(partner);
    setShowDeleteModal(true);
  };

  const confirmApprovePartner = (partner: PartnerSales, action: 'approve' | 'reject') => {
    setPartnerToApprove(partner);
    setApprovalAction(action);
    setShowApprovalModal(true);
  };

  const canManagePartners = (): boolean => {
    // Chỉ Manager mới có thể quản lý đối tác
    return userProfile?.role === 'manager';
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
            <p className="text-gray-600">Đang tải dữ liệu...</p>
          </div>
        </div>
      );
    }

    switch (currentView) {
      case 'detail':
        return selectedPartner ? (
          <PartnerDetail
            partner={selectedPartner}
            onBack={() => {
              setCurrentView('list');
              setSelectedPartner(null);
            }}
            onDelete={canManagePartners() ? () => confirmDeletePartner(selectedPartner) : undefined}
            onApprove={canManagePartners() && selectedPartner.status === 'pending' ? 
              (action) => confirmApprovePartner(selectedPartner, action) : undefined}
            timeFilter={timeFilter}
            onTimeFilterChange={setTimeFilter}
            currentUser={userProfile}
          />
        ) : null;
      case 'ranking':
        return (
          <PartnerRanking
            partners={partnersData.filter(p => p.status === 'approved')}
            onBack={() => setCurrentView('list')}
            onViewPartner={(partner) => {
              setSelectedPartner(partner);
              setCurrentView('detail');
            }}
            timeFilter={timeFilter}
            onTimeFilterChange={setTimeFilter}
          />
        );
      default:
        return (
          <>
            <PartnerList
              partners={filteredPartners}
              onViewPartner={(partner) => {
                setSelectedPartner(partner);
                setCurrentView('detail');
              }}
              onDeletePartner={canManagePartners() ? confirmDeletePartner : undefined}
              onApprovePartner={canManagePartners() ? confirmApprovePartner : undefined}
              onViewRanking={() => setCurrentView('ranking')}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              sortBy={sortBy}
              onSortByChange={setSortBy}
              timeFilter={timeFilter}
              onTimeFilterChange={setTimeFilter}
              currentUser={userProfile}
            />
            
            {/* Modal phê duyệt */}
            {showApprovalModal && partnerToApprove && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${
                      approvalAction === 'approve' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      <i className={`text-xl ${
                        approvalAction === 'approve' 
                          ? 'ri-user-add-line text-green-600' 
                          : 'ri-user-unfollow-line text-red-600'
                      }`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {approvalAction === 'approve' ? 'Phê duyệt đối tác' : 'Từ chối đối tác'}
                      </h3>
                      <p className="text-sm text-gray-600">Xác nhận hành động này</p>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <p className="text-gray-700">
                      Bạn có chắc chắn muốn {approvalAction === 'approve' ? 'phê duyệt' : 'từ chối'} đối tác{' '}
                      <strong>{partnerToApprove.partnerName}</strong>?
                    </p>
                    <div className="text-sm text-gray-500 mt-2 space-y-1">
                      <p>Email: {partnerToApprove.email}</p>
                      <p>Hoa hồng: {partnerToApprove.commissionRate}%</p>
                    </div>
                    <div className={`border rounded-lg p-3 mt-3 ${
                      approvalAction === 'approve' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}>
                      <p className={`text-sm ${
                        approvalAction === 'approve' ? 'text-green-800' : 'text-red-800'
                      }`}>
                        <i className={`mr-1 ${
                          approvalAction === 'approve' ? 'ri-information-line' : 'ri-warning-line'
                        }`}></i>
                        {approvalAction === 'approve' 
                          ? 'Đối tác sẽ có thể đăng nhập và bắt đầu bán hàng.'
                          : 'Đối tác sẽ không thể sử dụng hệ thống.'
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => {
                        setShowApprovalModal(false);
                        setPartnerToApprove(null);
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors whitespace-nowrap"
                    >
                      Hủy
                    </button>
                    <button
                      onClick={() => handleApprovePartner(partnerToApprove.partnerId, approvalAction)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                        approvalAction === 'approve'
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                    >
                      {approvalAction === 'approve' ? 'Phê duyệt' : 'Từ chối'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal xóa */}
            {showDeleteModal && partnerToDelete && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                      <i className="ri-user-unfollow-line text-red-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Xác nhận xóa đối tác</h3>
                      <p className="text-sm text-gray-600">Hành động này không thể hoàn tác</p>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <p className="text-gray-700">
                      Bạn có chắc chắn muốn xóa đối tác <strong>{partnerToDelete.partnerName}</strong>?
                    </p>
                    <div className="text-sm text-gray-500 mt-2 space-y-1">
                      <p>Email: {partnerToDelete.email}</p>
                      <p>Tổng doanh thu: {partnerToDelete.totalRevenue.toLocaleString('vi-VN')}đ</p>
                      <p>Tổng đơn hàng: {partnerToDelete.totalOrders}</p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                      <p className="text-yellow-800 text-sm">
                        <i className="ri-warning-line mr-1"></i>
                        Xóa đối tác sẽ ảnh hưởng đến các đơn hàng và báo cáo liên quan.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setPartnerToDelete(null);
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors whitespace-nowrap"
                    >
                      Hủy
                    </button>
                    <button
                      onClick={() => handleDeletePartner(partnerToDelete.partnerId)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
                    >
                      Xóa đối tác
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        );
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {renderContent()}
        {/* Create Partner Form */}
        {showCreateForm && (
          <CreatePartnerForm
            onSubmit={handleCreateSubmit}
            onCancel={() => setShowCreateForm(false)}
            isLoading={isLoading}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
